import axios, { AxiosRequestConfig } from "axios"
import axiosBase, { AxiosError, AxiosInstance } from "axios"
import qs from "qs"

import { TApiResponse } from "./TApiResponse"

const abortController = new AbortController()

/**
 * 初期設定用
 */
export type requestProperty = AxiosRequestConfig & {
  baseUrl?: string
}

class ConnectApi {
  axios: AxiosInstance
  authToken: string | null

  constructor(initData: requestProperty = {}) {
    //TODO: 一時的にtokenをローカルストレージに入れてみる
    this.authToken = localStorage.getItem("authToken")
    this.axios = axiosBase.create({
      signal: abortController.signal,
      baseURL: initData.baseUrl
        ? initData.baseUrl
        : process.env.REACT_APP_API_BASE_URL,
      headers: initData.headers
        ? {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            ...initData.headers,
          }
        : {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
      responseType: initData.responseType ? initData.responseType : "json",
      withCredentials: true,
    })
  }

  getInner = async <T>(path: string, params?: any, signal?: AbortSignal) => {
    try {
      return await this.axios.get(path, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: "repeat" })
        },
        headers: {
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<T>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  postInner = async <T, G>(
    path: string,
    data: T | null,
    signal?: AbortSignal
  ) => {
    try {
      return await this.axios.post(path, data && data, {
        headers: {
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<G>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  putInner = async <T, G>(path: string, data: T, signal?: AbortSignal) => {
    try {
      return await this.axios.put(path, data, {
        headers: {
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<G>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  deleteInner = async <T>(path: string, data?: T, signal?: AbortSignal) => {
    try {
      return await this.axios.delete(path, {
        data,
        headers: {
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<T>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  buildFormData = (formData: FormData, data: any, parentKey: string) => {
    if (
      data &&
      typeof data === "object" &&
      !(data instanceof Date) &&
      !(data instanceof Blob) &&
      !(data instanceof File)
    ) {
      if (data instanceof Array) {
        data.forEach((value, index) => {
          this.buildFormData(
            formData,
            data[index],
            parentKey ? `${parentKey}[${index}]` : `[${index}]`
          )
        })
      } else {
        Object.keys(data).forEach((key) => {
          this.buildFormData(
            formData,
            data[key],
            parentKey ? `${parentKey}.${key}` : key
          )
        })
      }
    } else {
      const value = data == null ? "" : data
      formData.append(parentKey, value)
    }
    return formData
  }

  toFormData = (data: any): FormData => {
    let formData = new FormData()
    this.buildFormData(formData, data, "")
    return formData
  }

  postFormDataInner = async <T, G>(
    path: string,
    data: T,
    signal?: AbortSignal
  ) => {
    try {
      const formData = this.toFormData(data)
      return await this.axios.post(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<G>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  putFormDataInner = async <T, G>(
    path: string,
    data: T,
    signal?: AbortSignal
  ) => {
    try {
      const formData = this.toFormData(data)
      return await this.axios.put(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-AuthToken": this.authToken ? this.authToken : "",
        },
        signal,
      })
    } catch (err) {
      if (typeof err !== "object" || err == null) throw err
      const error = err as AxiosError<TApiResponse<G>>
      if (error.isAxiosError && !axios.isCancel(error)) {
        switch (error.response?.status) {
          case 401:
          case 403:
            this.failedToken()
            break
          default:
            break
        }
      }
      throw error.response?.data
    }
  }

  /**
   * トークン認証失敗時の動作
   */
  failedToken = () => {
    abortController.abort()
    alert("ログアウトしました。もう一度ログインしてください。")
    localStorage.clear()

    window.location.href = "/login"
  }
}

export default ConnectApi
